import React, { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CurrencyDisplay, CurrencyInput, KgDisplay, KgInput, NegativeKgInput } from "../../../Utils/Inputs";
import { MonthHeader } from "../../../Utils/TableComponents";
import { getNextMonth } from "../../../Utils/Utils";

export default function LatexProcesoMonth(props) {
    const { month, monthIndex } = props;

    const { setValue, control, getValues } = useFormContext();

    const isFirstTable = month === 'enero';
    const LatexDataCostoMP = useWatch({
        control,
        name: 'ComparativoCostoMP.LatexData',
    });

    const BaseLatexProcesoDataCV = 'ComparativoCostoVentas.LatexProcesoData';

    // Entradas MP a producción
    const entradasMPAProduccionKgSecos = LatexDataCostoMP.SalidasAProduccion[month].KgSecos;
    const entradasMPAProduccionCostoKgSec = LatexDataCostoMP.SalidasAProduccion[month].CostoKgSec;
    const entradasMPAProduccionValorTotal = useMemo(() => entradasMPAProduccionKgSecos * entradasMPAProduccionCostoKgSec, [entradasMPAProduccionKgSecos, entradasMPAProduccionCostoKgSec]);

    // (+) Gtos. De compra de MP
    const produccionFcaKgSecos = LatexDataCostoMP.ProduccionFca[month].KgSecos;
    const gtosCompraCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.GtosDeCompraDeMP.${month}.CostoKgSec`,
    });
    const gtosCompraMPValorTotal = useMemo(() => gtosCompraCostoKgSec * (entradasMPAProduccionKgSecos - produccionFcaKgSecos), [gtosCompraCostoKgSec, entradasMPAProduccionKgSecos, produccionFcaKgSecos]);

    // Sub-total materia prima
    const subTotalMateriaPrimaKgSecos = useMemo(() => entradasMPAProduccionKgSecos, [entradasMPAProduccionKgSecos]);
    const subtoTalMateriaPrimaValorTotal = useMemo(() => entradasMPAProduccionValorTotal + gtosCompraMPValorTotal, [entradasMPAProduccionValorTotal, gtosCompraMPValorTotal]);
    const subTotalMateriaPrimaCostoKgSec = useMemo(() => subtoTalMateriaPrimaValorTotal / subTotalMateriaPrimaKgSecos, [subtoTalMateriaPrimaValorTotal, subTotalMateriaPrimaKgSecos]);

    // (-) SKIM
    const skimKgSecos = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.SKIM.${month}.KgSecos`,
    });
    const skimCostoKgSec = subTotalMateriaPrimaCostoKgSec;
    const skimValorTotal = useMemo(() => skimKgSecos * skimCostoKgSec, [skimKgSecos, skimCostoKgSec]);

    // (-) Coágulo lavado de pilas/centrifugas
    const coaguloLavadoPCKgSecos = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CoaguloLavadoDePilasCentrifugas.${month}.KgSecos`,
    });
    const coaguloLavadoPCCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CoaguloLavadoDePilasCentrifugas.${month}.CostoKgSec`,
    });
    const coaguloLavadoPCValorTotal = useMemo(() => coaguloLavadoPCKgSecos * coaguloLavadoPCCostoKgSec, [coaguloLavadoPCKgSecos, coaguloLavadoPCCostoKgSec]);

    // (-) Coágulo de Trampas
    const coaguloDeTrampasKgSecos = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CoaguloDeTrampas.${month}.KgSecos`,
    });
    const coaguloDeTrampasCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CoaguloDeTrampas.${month}.CostoKgSec`,
    });
    const coaguloDeTrampasValorTotal = useMemo(() => coaguloDeTrampasKgSecos * coaguloDeTrampasCostoKgSec, [coaguloDeTrampasKgSecos, coaguloDeTrampasCostoKgSec]);

    // (=) MP transf. En PP
    const mpTransfEnPPKgSecos = useMemo(() => entradasMPAProduccionKgSecos - skimKgSecos - coaguloDeTrampasKgSecos - coaguloLavadoPCKgSecos, [entradasMPAProduccionKgSecos, skimKgSecos, coaguloDeTrampasKgSecos, coaguloLavadoPCKgSecos]);
    const mpTransfEnPPValorTotal = useMemo(() => entradasMPAProduccionValorTotal + gtosCompraMPValorTotal - skimValorTotal, [entradasMPAProduccionValorTotal, gtosCompraMPValorTotal, skimValorTotal]);
    const mpTransfEnPPCostoKgSec = useMemo(() => mpTransfEnPPValorTotal / mpTransfEnPPKgSecos, [mpTransfEnPPValorTotal, mpTransfEnPPKgSecos]);

    // (+) Costo de proceso de centrifugado
    const costoProcesoCentrifugadoCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CostoProcesoCentrifugado.${month}.CostoKgSec`,
    });

    // (=) PP costeado
    const ppCosteadoKgSecos = mpTransfEnPPKgSecos;
    const ppCosteadoCostoKgSec = useMemo(() => mpTransfEnPPCostoKgSec + costoProcesoCentrifugadoCostoKgSec, [mpTransfEnPPCostoKgSec, costoProcesoCentrifugadoCostoKgSec]);
    const ppCosteadoValorTotal = useMemo(() => ppCosteadoKgSecos * ppCosteadoCostoKgSec, [ppCosteadoKgSecos, ppCosteadoCostoKgSec]);

    // (+) Inv. Inicial de PP
    const invInicialPPKgSecos = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.InvInicialDePP.${month}.KgSecos`
    });
    const invInicialPPCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.InvInicialDePP.${month}.CostoKgSec`
    });
    const invInicialPPValorTotal = useMemo(() => invInicialPPKgSecos * invInicialPPCostoKgSec, [invInicialPPKgSecos, invInicialPPCostoKgSec]);

    // (+) Latex de maquila x formula
    const latexDeMaquilaXFormulaRow = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.LatexDeMaquilaXFormula.${month}.KgSecos`
    });
    const latexDeMaquilaCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.LatexDeMaquilaXFormula.${month}.CostoKgSec`
    });
    const latexDeMaquilaValorTotal = useMemo(() => latexDeMaquilaXFormulaRow * latexDeMaquilaCostoKgSec, [latexDeMaquilaXFormulaRow, latexDeMaquilaCostoKgSec]);

    // (=) Disponible para Vta
    const disponibleParaVtaKgSecos = useMemo(() => ppCosteadoKgSecos + invInicialPPKgSecos + (latexDeMaquilaXFormulaRow || 0), [ppCosteadoKgSecos, invInicialPPKgSecos, latexDeMaquilaXFormulaRow]);
    const disponibleParaVtaValorTotal = useMemo(() => ppCosteadoValorTotal + invInicialPPValorTotal + (latexDeMaquilaValorTotal || 0), [ppCosteadoValorTotal, invInicialPPValorTotal, latexDeMaquilaValorTotal]);
    const disponibleParaVtaCostoKgSec = useMemo(() => disponibleParaVtaValorTotal / disponibleParaVtaKgSecos, [disponibleParaVtaValorTotal, disponibleParaVtaKgSecos]);

    // (-) Mermas
    const Perdida1PorcientoKgSecos = useMemo(() => entradasMPAProduccionKgSecos * -0.01, [entradasMPAProduccionKgSecos]);
    const mermasCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.Mermas.${month}.CostoKgSec`
    });
    
    const Perdida1PorcientoValorTotal = useMemo(() => Perdida1PorcientoKgSecos * (mermasCostoKgSec || 0), [Perdida1PorcientoKgSecos, mermasCostoKgSec]);

    // (-) Coágulos centrifugado
    const coagulosCentrifugadoKgSecos = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CoagulosCentrifugado.${month}.KgSecos`
    });
    const coagulosCentrifugadoCostoKgSec = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CoagulosCentrifugado.${month}.CostoKgSec`
    });
    const coagulosCentrifugadoValorTotal = useMemo(() => coagulosCentrifugadoKgSecos * coagulosCentrifugadoCostoKgSec, [coagulosCentrifugadoKgSecos, coagulosCentrifugadoCostoKgSec]);

    // (+) Comparas de PT 
    const ComprasDePTkgSecos = useWatch ({
        control,
        name: `${BaseLatexProcesoDataCV}.ComprasDePT.${month}.KgSecos`
    })
    const ComprasDePTCostoKgSec = useWatch ({
        control,
        name: `${BaseLatexProcesoDataCV}.ComprasDePT.${month}.CostoKgSec`
    })

    const ComprasDePTValorTotal = useMemo(() => ComprasDePTkgSecos * ComprasDePTCostoKgSec, [ComprasDePTkgSecos, ComprasDePTCostoKgSec])


    // (-) Costo Vta. Del Mes
    const costoVtaDelMesKgSecos = useWatch({
        control,
        name: `${BaseLatexProcesoDataCV}.CostoVtaDelMes.${month}.KgSecos`
    });

    const costoVtaDelMesKgSecosAbs = Math.abs(costoVtaDelMesKgSecos || 0);
    const ppAlaVentaKgSecos = costoVtaDelMesKgSecosAbs;
    const ppAlaVentaCostoKgSec = useMemo(() => disponibleParaVtaCostoKgSec, [disponibleParaVtaCostoKgSec]);
    const ppAlaVentaValorTotal = useMemo(() => ppAlaVentaKgSecos * ppAlaVentaCostoKgSec, [ppAlaVentaKgSecos, ppAlaVentaCostoKgSec]);

    // (=) Inv. Final de PT
    const invFinalPTKgSecos = useMemo(() => disponibleParaVtaKgSecos + (coagulosCentrifugadoKgSecos || 0) + Perdida1PorcientoKgSecos + (ComprasDePTkgSecos || 0), [ComprasDePTkgSecos,disponibleParaVtaKgSecos, coagulosCentrifugadoKgSecos, Perdida1PorcientoKgSecos]);
    const invFinalPTValorTotal = useMemo(() => disponibleParaVtaValorTotal - coagulosCentrifugadoValorTotal - Perdida1PorcientoValorTotal, [disponibleParaVtaValorTotal, coagulosCentrifugadoValorTotal, Perdida1PorcientoValorTotal]);
    const invFinalPTCostoKgSec = useMemo(() => invFinalPTValorTotal / invFinalPTKgSecos, [invFinalPTValorTotal, invFinalPTKgSecos]);

    // (=) Prod. Termin. Final

    const prodTerminFinalKgSecos = useMemo(() => invFinalPTKgSecos - costoVtaDelMesKgSecosAbs, [invFinalPTKgSecos, costoVtaDelMesKgSecosAbs]);
    const prodTerminFinalCostoKgSec = useMemo(() => invFinalPTCostoKgSec, [invFinalPTCostoKgSec]);
    const prodTerminFinalValorTotal = useMemo(() => prodTerminFinalKgSecos * prodTerminFinalCostoKgSec, [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec]);



    // CASOS ESPECIALES
    // Inv. Inicial de PP para enero y meses posteriores
    let invInicialDePPRow = null;
    if (!isFirstTable) {
        invInicialDePPRow = (
            <tr>
                <td><KgDisplay value={invInicialPPKgSecos} /></td>
                <td><CurrencyDisplay value={invInicialPPCostoKgSec} decimalScale={5} /></td>
                <td><CurrencyDisplay value={invInicialPPValorTotal} /></td>
            </tr>
        );
    } else {
        invInicialDePPRow = (
            <tr>
                <td>
                    <KgInput
                        name={`${BaseLatexProcesoDataCV}.InvInicialDePP.${month}.KgSecos`} />
                </td>
                <td>
                    <CurrencyInput
                        name={`${BaseLatexProcesoDataCV}.InvInicialDePP.${month}.CostoKgSec`}
                        decimalScale={5} />
                </td>
                <td><CurrencyDisplay value={invInicialPPValorTotal} /></td>
            </tr>
        );
    }

    // Actualizar datos de coágulos para tabla SOLIDOS de COSTO DE MP
    useEffect(() => {
        // Coágulo lavado pilas/centrifugas
        setValue(`ComparativoCostoMP.SolidosData.CoaguloLavadoPilasCentrifugas.${month}.KgSecos`, coaguloLavadoPCKgSecos);
        // Coágulos de trampas
        setValue(`ComparativoCostoMP.SolidosData.CoagulosDeTrampas.${month}.KgSecos`, coaguloDeTrampasKgSecos);
        // Coágulos centrifugado
        setValue(`ComparativoCostoMP.SolidosData.CoagulosCentrifugado.${month}.KgSecos`, coagulosCentrifugadoKgSecos);
    }, [
        coaguloLavadoPCKgSecos, coaguloDeTrampasKgSecos, coagulosCentrifugadoKgSecos,
        month, setValue
    ]);

    // Actualizar los valores de SKIM para tabla LATEX producto terminado de COSTO DE VENTAS
    // Para guardar Costo Vta Del Mes
    useEffect(() => {
        setValue(`${BaseLatexProcesoDataCV}.CostoVtaDelMes.${month}.CostoKgSec`, ppAlaVentaCostoKgSec);
    }, [ppAlaVentaCostoKgSec, month, setValue]);

    useEffect(() => {
        setValue(`${BaseLatexProcesoDataCV}.SKIM.${month}.CostoKgSec`, skimCostoKgSec);
    }, [
        skimCostoKgSec,
        month, setValue
    ])

    // Para guardar Inv. Final de PP
    useEffect(() => {
        setValue(`${BaseLatexProcesoDataCV}.InvFinalDePP.${month}.KgSecos`, invFinalPTKgSecos);
        setValue(`${BaseLatexProcesoDataCV}.InvFinalDePP.${month}.CostoKgSec`, invFinalPTCostoKgSec);
    }, [invFinalPTKgSecos, invFinalPTCostoKgSec, month, setValue]);

    useEffect(() => {
        setValue(`${BaseLatexProcesoDataCV}.ProdTerminFinal.${month}.KgSecos`, prodTerminFinalKgSecos);
        setValue(`${BaseLatexProcesoDataCV}.ProdTerminFinal.${month}.CostoKgSec`, prodTerminFinalCostoKgSec);
    }, [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec, prodTerminFinalValorTotal, month, setValue]);

    // Actualizar Inv. Inicial de PP para meses posteriores
    useEffect(() => {
        const nextMonth = getNextMonth(monthIndex);
        if (nextMonth === 'enero') return;
        
        // Solo verificar que no sean NaN (permitir 0, null se convierte a 0)
        const kgSecos = prodTerminFinalKgSecos || 0;
        const costoKgSec = prodTerminFinalCostoKgSec || 0;
        
        if (isNaN(kgSecos) || isNaN(costoKgSec)) return;
        
        // Siempre actualizar - el backend debería manejar la lógica de cuándo aplicar
        setValue(`${BaseLatexProcesoDataCV}.InvInicialDePP.${nextMonth}.KgSecos`, kgSecos);
        setValue(`${BaseLatexProcesoDataCV}.InvInicialDePP.${nextMonth}.CostoKgSec`, costoKgSec);
    }, [prodTerminFinalKgSecos, prodTerminFinalCostoKgSec, monthIndex, setValue]);
    

    // Actualizar los valores "Del mes" para tabla SKIM de COSTO DE MP
    useEffect(() => {
        setValue(`ComparativoCostoMP.SKIMData.DelMes.${month}.KgSecos`, skimKgSecos);
        setValue(`ComparativoCostoMP.SKIMData.DelMes.${month}.CostoKgSec`, skimCostoKgSec);
    }, [
        skimKgSecos, skimCostoKgSec,
        month, setValue
    ])

    return (
        <table key={monthIndex}>
            <MonthHeader month={month} monthIndex={monthIndex} />
            <tbody>
                {/* Entradas MP a producción */}
                <tr>
                    <td><KgDisplay value={entradasMPAProduccionKgSecos} /></td>
                    <td><CurrencyDisplay value={entradasMPAProduccionCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={entradasMPAProduccionValorTotal} /></td>
                </tr>
                {/* (+) Gtos. De compra de MP */}
                <tr>
                    <td></td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.GtosDeCompraDeMP.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={gtosCompraMPValorTotal} /></td>
                </tr>
                {/* Sub-total materia prima */}
                <tr className="celda-totales">
                    <td><KgDisplay value={subTotalMateriaPrimaKgSecos} /></td>
                    <td><CurrencyDisplay value={subTotalMateriaPrimaCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={subtoTalMateriaPrimaValorTotal} /></td>
                </tr>
                {/* (-) SKIM */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseLatexProcesoDataCV}.SKIM.${month}.KgSecos`} />
                    </td>
                    <td><CurrencyDisplay value={skimCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={skimValorTotal} /></td>
                </tr>
                {/* (-) Coágulo lavado de pilas/centrifugas */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseLatexProcesoDataCV}.CoaguloLavadoDePilasCentrifugas.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.CoaguloLavadoDePilasCentrifugas.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={coaguloLavadoPCValorTotal} /></td>
                </tr>
                {/* (-) Coágulo de Trampas */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseLatexProcesoDataCV}.CoaguloDeTrampas.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.CoaguloDeTrampas.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={coaguloDeTrampasValorTotal} /></td>
                </tr>
                {/* (=) MP transf. En PP */}
                <tr className="celda-totales">
                    <td><KgDisplay value={mpTransfEnPPKgSecos} /></td>
                    <td><CurrencyDisplay value={mpTransfEnPPCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={mpTransfEnPPValorTotal} /></td>
                </tr>
                {/* (+) Costo de proceso de centrifugado */}
                <tr>
                    <td></td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.CostoProcesoCentrifugado.${month}.CostoKgSec`}
                            decimalScale={5}
                        />
                    </td>
                    <td></td>
                </tr>
                {/* (=) PP costeado */}
                <tr className="celda-totales">
                    <td><KgDisplay value={ppCosteadoKgSecos} /></td>
                    <td><CurrencyDisplay value={ppCosteadoCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={ppCosteadoValorTotal} /></td>
                </tr>
                {/* (+) Inv. Inicial de PP */}

                {invInicialDePPRow}
                {/* Latex de maquila x formula */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseLatexProcesoDataCV}.LatexDeMaquilaXFormula.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.LatexDeMaquilaXFormula.${month}.CostoKgSec`} decimalScale={5} />
                    </td>
                    <td>
                        <CurrencyDisplay value={latexDeMaquilaValorTotal} />
                    </td>
                </tr>
                
                {/* (=) Disponible para Vta */}
                <tr className="celda-totales">
                    <td><KgDisplay value={disponibleParaVtaKgSecos} /></td>
                    <td><CurrencyDisplay value={disponibleParaVtaCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={disponibleParaVtaValorTotal} /></td>
                </tr>
                {/* (-) Perdida (1%) */}
                <tr>
                    <td><KgDisplay value={Perdida1PorcientoKgSecos} /></td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.Perdida1Porciento.${month}.CostoKgSec`} decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={Perdida1PorcientoValorTotal} /></td>
                </tr>
                {/* (-) Coágulos centrifugado */}
                <tr>
                    <td>
                        <NegativeKgInput
                            name={`${BaseLatexProcesoDataCV}.CoagulosCentrifugado.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyInput
                            name={`${BaseLatexProcesoDataCV}.CoagulosCentrifugado.${month}.CostoKgSec`}
                            decimalScale={5} />
                    </td>
                    <td><CurrencyDisplay value={coagulosCentrifugadoValorTotal} /></td>


                </tr>
                {/* (+) Compras de PT*/}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseLatexProcesoDataCV}.ComprasDePT.${month}.KgSecos`} />
                    </td>
                    <td> 
                        <CurrencyInput  
                            name={`${BaseLatexProcesoDataCV}.ComprasDePT.${month}.CostoKgSec`}
                            decimalScale={5} /></td>
                    <td><CurrencyDisplay value={ComprasDePTValorTotal} /></td>
                </tr>


                {/* (=) Inv. Final de PT */}
                <tr className="celda-totales">
                    <td><KgDisplay value={invFinalPTKgSecos} /></td>
                    <td><CurrencyDisplay value={invFinalPTCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={invFinalPTValorTotal} /></td>
                </tr>
                {/* (-) Costo Vta. Del Mes */}
                <tr>
                    <td>
                        <KgInput
                            name={`${BaseLatexProcesoDataCV}.CostoVtaDelMes.${month}.KgSecos`} />
                    </td>
                    <td>
                        <CurrencyDisplay value={ppAlaVentaCostoKgSec} decimalScale={5}/>
                    </td>
                    <td><CurrencyDisplay value={ppAlaVentaValorTotal} /></td>
                </tr>
                {/* (=) Prod. Termin. Final */}
                <tr className="celda-totales">
                    <td><KgDisplay value={prodTerminFinalKgSecos} /></td>
                    <td><CurrencyDisplay value={prodTerminFinalCostoKgSec} decimalScale={5} /></td>
                    <td><CurrencyDisplay value={prodTerminFinalValorTotal} /></td>
                </tr>

            </tbody>
        </table>
    );
}
